import { Button, Card, Drawer, Form, Input, List, Popconfirm, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import MongerlabCustomerService from "../../services/MongerlabCustomerService";
import { IMongerlabCustomerNote } from "../../types/MongerlabCustomer";
import { can, convertDate, openNotification } from "../../utils";

const { TextArea } = Input;
const { Text } = Typography;

interface NotesProps {
  customerId: number;
}

const Notes = ({ customerId }: NotesProps) => {
  const [notes, setNotes] = useState<IMongerlabCustomerNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form] = Form.useForm();

  const loadNotes = () => {
    setLoading(true);
    MongerlabCustomerService.getNotes(customerId)
      .then((response) => setNotes(response.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadNotes();
  }, [customerId]);

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      await MongerlabCustomerService.createNote(customerId, values);
      openNotification("success", "Success", "Note created");
      form.resetFields();
      setDrawerOpen(false);
      loadNotes();
    } catch (e) {
      console.error(e);
    }
  };

  const handleDelete = (noteId: number) => {
    MongerlabCustomerService.deleteNote(noteId)
      .then(() => {
        openNotification("success", "Success", "Note deleted");
        loadNotes();
      })
      .catch(console.error);
  };

  return (
    <Card
      title="Notes"
      extra={
        can("mongerlab-customers:notes:create") && (
          <Button type="primary" size="small" onClick={() => setDrawerOpen(true)}>
            Add Note
          </Button>
        )
      }
    >
      <List
        loading={loading}
        dataSource={notes}
        locale={{ emptyText: "No notes yet" }}
        renderItem={(item) => (
          <List.Item
            actions={
              can("mongerlab-customers:notes:delete")
                ? [
                    <Popconfirm title="Delete note?" onConfirm={() => handleDelete(item.id)}>
                      <Button danger size="small">
                        Delete
                      </Button>
                    </Popconfirm>,
                  ]
                : []
            }
          >
            <List.Item.Meta
              title={
                <Space>
                  <Text strong>{item.user?.username || "Unknown"}</Text>
                  <Text type="secondary">{convertDate(item.createdAt)}</Text>
                </Space>
              }
              description={item.description}
            />
          </List.Item>
        )}
      />

      <Drawer title="Add Note" open={drawerOpen} onClose={() => setDrawerOpen(false)} width={400}>
        <Form form={form} layout="vertical" onFinish={handleCreate}>
          <Form.Item name="description" label="Note" rules={[{ required: true, message: "Required" }]}>
            <TextArea rows={6} />
          </Form.Item>
          <Button type="primary" htmlType="submit">
            Save
          </Button>
        </Form>
      </Drawer>
    </Card>
  );
};

export default Notes;
